import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  buildCyberTracerExportFilename,
  dedupeDebugBundleResults,
  exportGraphForMaltego,
  exportToPDF,
  sourceLabelFromResult,
} from '../services/intelligence_exporter';
import { getApiBaseUrl } from '../config/apiBase';
import { trackEvent } from '../utils/analytics';
import { isCoarseMobileClient } from '../utils/mobilePdfShare';
import MobilePdfShareOverlay from './MobilePdfShareOverlay';

function rowFingerprint(r) {
  if (!r || typeof r !== 'object') return '';
  return [r.url, r.source, r.title, r.type, r.platform].map((x) => String(x ?? '')).join('\u0001');
}

/**
 * Visual priority only — never used to filter rows. Default neutral (grey) when fields are missing.
 * @returns {'verified' | 'risk' | 'neutral'}
 */
function prioritySignal(r) {
  if (!r || typeof r !== 'object') return 'neutral';

  const hasExplicitScore = r.score != null && r.score !== '';
  const scoreNum = hasExplicitScore ? Number(r.score) : NaN;

  const rawRisk = r.risk_score ?? r.details?.risk_score;
  const hasExplicitRisk = rawRisk != null && rawRisk !== '';
  const riskNum = hasExplicitRisk ? Number(rawRisk) : NaN;

  if (r.is_primary_identity === true) return 'verified';
  if (hasExplicitScore && !Number.isNaN(scoreNum) && scoreNum > 90) return 'verified';
  if (r.source === 'ghunt' && r.platform === 'Google Account Info') return 'verified';
  if (r.source === 'hibp_status' && r.status === 'clean') return 'verified';

  if (hasExplicitRisk && !Number.isNaN(riskNum) && riskNum >= 80) return 'risk';
  if (r.risk_level === 'CRITICAL') return 'risk';
  if (typeof r.malicious === 'number' && r.malicious > 0) return 'risk';
  if (r.source === 'hibp') return 'risk';
  if (r.source === 'hibp_status' && r.status === 'breached') return 'risk';
  if (r.ui_bucket === 'negative_news') return 'risk';
  if (r.ui_bucket === 'compliance_alert' && String(r.risk_level || '').toUpperCase() === 'CRITICAL') return 'risk';
  if (hasExplicitRisk && !Number.isNaN(riskNum) && riskNum >= 50) return 'risk';

  return 'neutral';
}

function PriorityDot({ signal }) {
  const map = {
    verified: { bg: 'bg-emerald-500', ring: 'ring-emerald-500/30', label: 'Verified / high confidence' },
    risk: { bg: 'bg-red-500', ring: 'ring-red-500/30', label: 'Risk / scam signal' },
    neutral: { bg: 'bg-slate-500', ring: 'ring-slate-500/40', label: 'Neutral — review manually' },
  };
  const c = map[signal] || map.neutral;
  return (
    <span
      className={`inline-flex h-2 w-2 shrink-0 rounded-full ring-2 ${c.bg} ${c.ring}`}
      title={c.label}
      aria-label={c.label}
      role="img"
    />
  );
}

function PriorityBadge({ signal }) {
  if (signal === 'neutral') return null;
  const text = signal === 'verified' ? 'Verified' : 'Risk';
  const cls =
    signal === 'verified'
      ? 'border-emerald-500/40 bg-emerald-950/40 text-emerald-300'
      : 'border-rose-500/40 bg-rose-950/40 text-rose-300';
  return (
    <span className={`rounded px-1.5 py-0 text-[9px] font-bold uppercase tracking-wide ${cls}`}>{text}</span>
  );
}

function reasonCodeLabel(code) {
  const map = {
    HIBP_CLEAN: 'HIBP clean (no breach correlated)',
    BREACH_SIGNAL_PRESENT: 'Breach signal present in HIBP correlation',
    NO_PUBLIC_SIGNALS: 'No relevant public web signals detected',
    POSSIBLE_TELEMARKETING: 'Pattern matches possible telemarketing',
    SCAM_CLUSTER_SIGNAL: 'Cluster of risky reverse-lookup sources',
    BUSINESS_WEB_FOOTPRINT: 'Business/public procurement footprint found',
    POSITIVE_WEB_FOOTPRINT: 'Positive web footprint found',
    OFFICIAL_ENTITY_MATCH: 'Official entity source matched',
  };
  return map[code] || String(code || '').replaceAll('_', ' ').toLowerCase();
}

/**
 * Investigation Review Center — slide-over panel for manual review, export, and Telegram PDF delivery.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Array<object>} props.results
 * @param {Array} props.logs
 * @param {string} props.target
 * @param {string} props.moduleName
 * @param {(result: object) => void} [props.onExcludeFromFeed] — when user removes a row and "sync to feed" is on
 * @param {(result: object) => string} [props.getDisplayName]
 * @param {{emails?: string[], associated_urls?: string[], associated_urls_partial?: boolean[]}} [props.extractedLeads]
 * @param {string} [props.apiBaseUrl] - Flask API origin (same as SSE), default REACT_APP_API_BASE_URL or current host on port 5000
 */
const DEFAULT_REPORT_API_BASE = getApiBaseUrl();

export default function ReportModal({
  isOpen,
  onClose,
  results = [],
  logs = [],
  target = '',
  moduleName = 'Intelligence',
  onExcludeFromFeed,
  getDisplayName,
  extractedLeads,
  apiBaseUrl = DEFAULT_REPORT_API_BASE,
}) {
  const [entered, setEntered] = useState(false);
  const [stagedResults, setStagedResults] = useState([]);
  const [telegramUserId, setTelegramUserId] = useState('');
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [telegramFeedback, setTelegramFeedback] = useState(null);
  const [syncRemovalsToFeed, setSyncRemovalsToFeed] = useState(false);
  const [stagedLeads, setStagedLeads] = useState({ emails: [], associated_urls: [], associated_urls_partial: [] });
  const [copiedGuide, setCopiedGuide] = useState(false);
  const [decisionTraceOpen, setDecisionTraceOpen] = useState(false);
  /** @type {[{ blob: Blob, filename: string } | null, function]} */
  const [pdfSharePreview, setPdfSharePreview] = useState(null);
  /** Fingerprints unchecked for the report (rows stay visible, greyed out). */
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());
  const debugImportInputRef = useRef(null);

  const wasOpenRef = useRef(false);
  /** @deprecated kept for feed-sync when unchecking with "hide from feed" on */
  const removedRowKeysRef = useRef(new Set());

  const handleClose = useCallback(() => {
    setEntered(false);
    window.setTimeout(() => onClose(), 300);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setEntered(false);
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    return undefined;
  }, [isOpen]);

  /**
   * While the panel is open, mirror `results` into `stagedResults` (full list).
   * Inclusion is controlled via `excludedKeys` (grey-out), not row deletion.
   */
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    if (justOpened) {
      removedRowKeysRef.current = new Set();
      setExcludedKeys(new Set());
      setTelegramUserId('');
      setTelegramBusy(false);
      setTelegramFeedback(null);
      setSyncRemovalsToFeed(false);
      setDecisionTraceOpen(false);
      setPdfSharePreview(null);
    }
    wasOpenRef.current = true;

    const list = Array.isArray(results) ? results : [];
    const cloned = list.map((r) => (r != null && typeof r === 'object' ? { ...r } : {}));
    setStagedResults(cloned);
    setStagedLeads({
      emails: Array.isArray(extractedLeads?.emails)
        ? extractedLeads.emails.map((x) => String(x || '').trim()).filter(Boolean)
        : [],
      phones: Array.isArray(extractedLeads?.phones)
        ? extractedLeads.phones.map((x) => String(x || '').trim()).filter(Boolean)
        : [],
      cuis: Array.isArray(extractedLeads?.cuis)
        ? extractedLeads.cuis.map((x) => String(x || '').trim()).filter(Boolean)
        : [],
      associated_urls: Array.isArray(extractedLeads?.associated_urls)
        ? extractedLeads.associated_urls.map((x) => String(x || '').trim()).filter(Boolean)
        : [],
      associated_urls_partial: Array.isArray(extractedLeads?.associated_urls_partial)
        ? extractedLeads.associated_urls_partial.map((x) => Boolean(x))
        : [],
      pivots: Array.isArray(extractedLeads?.pivots)
        ? extractedLeads.pivots.filter((p) => p && typeof p === 'object').map((p) => ({ ...p }))
        : [],
    });
  }, [isOpen, results, extractedLeads]);

  const selectedResults = useMemo(
    () => (stagedResults || []).filter((r) => r && !excludedKeys.has(rowFingerprint(r))),
    [stagedResults, excludedKeys],
  );
  const selectedCount = selectedResults.length;
  const canExportReport = selectedCount > 0;

  const decisionTrace = useMemo(() => {
    const firstAuditRow = selectedResults.find(
      (r) => r && typeof r === 'object' && r.audit && Array.isArray(r.audit.reason_codes),
    );
    const codes = Array.isArray(firstAuditRow?.audit?.reason_codes) ? firstAuditRow.audit.reason_codes : [];
    const telemetry = firstAuditRow?.audit?.provider_telemetry || null;
    const providers = Array.isArray(telemetry?.providers_used) ? telemetry.providers_used : [];
    return { codes, providers, fallbackUsed: Boolean(telemetry?.fallback_used) };
  }, [selectedResults]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  const toggleRowIncluded = (row) => {
    if (!row || typeof row !== 'object') return;
    const fp = rowFingerprint(row);
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fp)) {
        next.delete(fp);
      } else {
        next.add(fp);
        if (syncRemovalsToFeed && typeof onExcludeFromFeed === 'function') {
          onExcludeFromFeed(row);
        }
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setExcludedKeys(new Set());
  };

  const handleDeselectAll = () => {
    const all = new Set();
    for (const row of stagedResults) {
      if (row && typeof row === 'object') {
        const fp = rowFingerprint(row);
        all.add(fp);
        if (syncRemovalsToFeed && typeof onExcludeFromFeed === 'function') {
          onExcludeFromFeed(row);
        }
      }
    }
    setExcludedKeys(all);
  };

  const handleDownloadFinalPdf = async () => {
    if (!canExportReport) return;
    try {
      const mobileShare = isCoarseMobileClient();
      if (mobileShare) {
        const blob = await exportToPDF({
          target,
          results: selectedResults,
          logs,
          moduleName,
          extractedLeads: stagedLeads,
          returnBlob: true,
        });
        if (!(blob instanceof Blob)) {
          throw new Error('PDF blob missing');
        }
        const filename = buildCyberTracerExportFilename({
          moduleName,
          target,
          extension: 'pdf',
        });
        setPdfSharePreview({ blob, filename });
        trackEvent('report_exported', {
          module: String(moduleName || '').toLowerCase() || 'unknown',
          format: 'pdf_mobile_share',
          results_count: selectedCount,
        });
        return;
      }
      await exportToPDF({
        target,
        results: selectedResults,
        logs,
        moduleName,
        extractedLeads: stagedLeads,
      });
      trackEvent('report_exported', {
        module: String(moduleName || '').toLowerCase() || 'unknown',
        format: 'pdf',
        results_count: selectedCount,
      });
    } catch (e) {
      if (typeof window !== 'undefined' && window.console) {
        console.error('[PDF] Export failed', e);
      }
    }
  };
  const handleExportMaltego = () => {
    if (!canExportReport) return;
    exportGraphForMaltego({ target, results: selectedResults, extractedLeads: stagedLeads, moduleName });
  };

  const handleExportDebugJson = () => {
    // Disabled in this release (button greyed out).
  };

  const handleImportDebugJson = async (ev) => {
    const file = ev?.target?.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const rows = Array.isArray(parsed?.results) ? parsed.results : [];
      const leads = parsed?.extractedLeads || {};
      setStagedResults(dedupeDebugBundleResults(rows.filter((r) => r && typeof r === 'object')));
      setStagedLeads({
        emails: Array.isArray(leads.emails) ? leads.emails.map((x) => String(x || '').trim()).filter(Boolean) : [],
        associated_urls: Array.isArray(leads.associated_urls)
          ? leads.associated_urls.map((x) => String(x || '').trim()).filter(Boolean)
          : [],
        associated_urls_partial: Array.isArray(leads.associated_urls_partial)
          ? leads.associated_urls_partial.map((x) => Boolean(x))
          : [],
      });
      removedRowKeysRef.current = new Set();
    } catch (e) {
      if (typeof window !== 'undefined' && window.console) {
        console.error('[ReportModal] Invalid debug JSON import', e);
      }
      if (typeof window !== 'undefined') {
        window.alert('Invalid debug JSON. Please import a valid wiw-debug bundle.');
      }
    } finally {
      if (ev?.target) ev.target.value = '';
    }
  };

  const handleSendPdfTelegram = async () => {
    const cid = String(telegramUserId || '').replace(/\s+/g, '');
    if (!/^-?\d{5,}$/.test(cid)) {
      setTelegramFeedback({ type: 'error', text: 'Enter a numeric Telegram chat ID (e.g. from @userinfobot).' });
      return;
    }
    const base = String(apiBaseUrl || '').replace(/\/$/, '');
    if (!base) {
      setTelegramFeedback({ type: 'error', text: 'API base URL is not configured.' });
      return;
    }
    setTelegramBusy(true);
    setTelegramFeedback(null);
    try {
      const blob = await exportToPDF({
        target,
        results: selectedResults,
        logs,
        moduleName,
        extractedLeads: stagedLeads,
        returnBlob: true,
      });
      if (!(blob instanceof Blob)) {
        setTelegramFeedback({ type: 'error', text: 'Could not build the PDF in the browser.' });
        return;
      }
      const filename = buildCyberTracerExportFilename({
        moduleName,
        target,
        extension: 'pdf',
      });
      const fd = new FormData();
      fd.append('chat_id', cid);
      if (typeof File !== 'undefined') {
        fd.append('pdf', new File([blob], filename, { type: 'application/pdf' }));
      } else {
        fd.append('pdf', blob, filename);
      }
      const res = await fetch(`${base}/send-report-pdf-telegram`, { method: 'POST', body: fd });
      let body = {};
      try {
        body = await res.json();
      } catch {
        body = {};
      }
      if (!res.ok || !body.ok) {
        setTelegramFeedback({
          type: 'error',
          text: typeof body.error === 'string' ? body.error : `Request failed (${res.status}).`,
        });
        return;
      }
      setTelegramFeedback({ type: 'ok', text: 'PDF sent to Telegram.' });
    } catch (e) {
      const msg = e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Network or PDF error.';
      setTelegramFeedback({ type: 'error', text: msg });
    } finally {
      setTelegramBusy(false);
    }
  };
  const handleCopyMaltegoGuide = async () => {
    const guideText = [
      'Maltego CE Import Steps',
      '1) Click "Export Local Graph" in WhoIsWho.',
      '2) In Maltego CE: Import Table -> load maltego-ce-...-entities.csv.',
      '3) Map entity_type and value (optional: label, source, risk_score).',
      '4) Import Table -> load maltego-ce-...-links.csv.',
      '5) Map source_id, target_id, relationship and link to imported entities.',
    ].join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(guideText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = guideText;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedGuide(true);
      window.setTimeout(() => setCopiedGuide(false), 1400);
    } catch {
      setCopiedGuide(false);
    }
  };

  const rowTitle = (r) => {
    if (r?.type === 'phone_mention') {
      const t = r.title && String(r.title).trim();
      if (t) return t;
      if (r.url) return String(r.url);
      return 'Web mention';
    }
    if (typeof getDisplayName === 'function') {
      try {
        return getDisplayName(r);
      } catch {
        /* fall through */
      }
    }
    if (r?.title) return String(r.title);
    return sourceLabelFromResult(r);
  };

  function renderResultRows() {
    if (!stagedResults.length) {
      return (
        <li className="rounded-lg border border-dashed border-slate-700 bg-slate-950/40 px-3 py-8 text-center">
          <p className="text-xs leading-relaxed text-slate-500">No results available.</p>
        </li>
      );
    }
    return stagedResults.map((r, idx) => {
      const sig = prioritySignal(r);
      const titleText = rowTitle(r);
      const titleStr = typeof titleText === 'string' ? titleText : String(titleText);
      const fp = rowFingerprint(r);
      const included = !excludedKeys.has(fp);
      return (
        <li
          key={`${fp}#${idx}`}
          className={`flex items-center gap-2 rounded-lg px-3 py-1.5 transition ${
            included
              ? 'text-slate-300 hover:bg-slate-800/40 hover:text-white'
              : 'bg-slate-900/30 text-slate-500 opacity-40 line-through'
          }`}
          data-testid="report-result-row"
          data-included={included ? 'true' : 'false'}
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 shrink-0 rounded border-slate-600 bg-slate-900 text-cyan-500 accent-cyan-500 focus:ring-cyan-500/40"
            checked={included}
            onChange={() => toggleRowIncluded(r)}
            aria-label={`${included ? 'Exclude' : 'Include'} ${titleStr} in report`}
            data-testid="report-row-checkbox"
          />
          <div className="min-w-0 flex-1 overflow-hidden">
            <div
              className={`line-clamp-1 max-w-full break-words text-xs font-semibold leading-snug ${
                included ? 'text-slate-100' : 'text-slate-500'
              }`}
              title={titleStr}
            >
              {titleStr}
            </div>
            <div className="mt-0.5 flex min-w-0 items-center gap-2">
              <PriorityDot signal={sig} />
              <span className="min-w-0 line-clamp-1 break-all text-[10px] text-slate-500">
                {sourceLabelFromResult(r)}
              </span>
              <PriorityBadge signal={sig} />
            </div>
          </div>
        </li>
      );
    });
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="investigation-review-title">
      <button
        type="button"
        className={`absolute inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity duration-300 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        aria-label="Close investigation review"
        onClick={handleClose}
      />

      <aside
        className={`fixed inset-x-3 top-[7.5vh] z-[60] mx-auto flex max-h-[85vh] w-auto max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl backdrop-blur-md transition-all duration-300 ease-out sm:inset-y-0 sm:right-0 sm:left-auto sm:top-0 sm:mx-0 sm:h-[100dvh] sm:max-h-[100dvh] sm:w-full sm:max-w-md sm:rounded-none sm:rounded-l-2xl sm:border-l sm:border-y-0 sm:border-r-0 lg:max-w-[420px] ${
          entered ? 'translate-y-0 opacity-100 sm:translate-x-0' : 'translate-y-4 opacity-0 sm:translate-x-full sm:translate-y-0 sm:opacity-100'
        }`}
      >
        <div className="sticky top-0 z-10 shrink-0 border-b border-slate-800 bg-slate-900/95 px-3 py-3 backdrop-blur-md sm:px-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 id="investigation-review-title" className="text-sm font-black uppercase tracking-[0.12em] text-slate-100">
                Investigation Review Center
              </h2>
              <p className="mt-0.5 text-[11px] text-slate-400">Manual review · export · graph</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100 touch-manipulation"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4">
            <div className="space-y-1 border-b border-slate-800 pb-3 text-sm text-slate-300">
              <p>
                <span className="font-semibold text-slate-100">Target:</span> {target || '—'}
              </p>
              <p className="text-[11px] uppercase tracking-widest text-slate-500">Module · {moduleName}</p>
            </div>

            <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-2.5 text-xs text-slate-300 transition hover:text-white">
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 rounded border-slate-600 bg-slate-900 text-cyan-500 accent-cyan-500 focus:ring-cyan-500/40"
                checked={syncRemovalsToFeed}
                onChange={(e) => setSyncRemovalsToFeed(e.target.checked)}
              />
              <span>
                <span className="font-semibold text-slate-200">Also hide from discovery feed</span>
                <span className="mt-0.5 block text-[11px] font-normal text-slate-500">
                  Off (default): unchecking only greys out the row in this report. On: unchecked rows are also
                  hidden in the main feed until the next scan.
                </span>
              </span>
            </label>

            <div className="mb-2 mt-5 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                Results in report ({selectedCount})
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300 transition hover:border-cyan-500/40 hover:text-cyan-200"
                  data-testid="report-select-all"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="rounded-lg border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-300 transition hover:border-slate-500 hover:text-white"
                  data-testid="report-deselect-all"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <ul className="max-h-72 space-y-0.5 overflow-y-auto pr-1">
              {renderResultRows()}
            </ul>

            {decisionTrace.codes.length > 0 && (
              <div className="mt-5 rounded-xl border border-slate-700 bg-slate-950/50">
                <button
                  type="button"
                  onClick={() => setDecisionTraceOpen((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                  aria-expanded={decisionTraceOpen}
                  data-testid="report-decision-trace-toggle"
                >
                  <h4 className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                    Decision Trace
                  </h4>
                  <span className="font-mono text-[10px] text-slate-500">
                    {decisionTraceOpen ? '[-]' : '[+]'}
                  </span>
                </button>
                {decisionTraceOpen ? (
                  <div className="border-t border-slate-800 px-3 pb-3 pt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {decisionTrace.codes.map((code, i) => (
                        <span
                          key={`${code}-${i}`}
                          className="rounded-md border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-slate-300"
                          title={String(code)}
                        >
                          {reasonCodeLabel(code)}
                        </span>
                      ))}
                    </div>
                    {decisionTrace.providers.length > 0 && (
                      <p className="mt-2 text-[10px] text-slate-500">
                        Sources: {decisionTrace.providers.join(', ')}
                        {decisionTrace.fallbackUsed ? ' (fallback used)' : ''}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            <div
              className="mt-6 border-t border-slate-800 pt-4 opacity-35"
              aria-disabled="true"
              data-testid="report-telegram-locked"
            >
              <h3 className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Telegram chat ID</h3>
              <input
                id="telegram-user-id"
                type="text"
                value={telegramUserId}
                onChange={(e) => setTelegramUserId(e.target.value)}
                placeholder="e.g. 123456789"
                className="w-full cursor-not-allowed rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600"
                autoComplete="off"
                disabled
              />
              <p className="mt-2 text-[10px] text-slate-500">Telegram delivery is disabled in this release.</p>
            </div>

            <div
              className="mt-4 cursor-not-allowed rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-2 opacity-35"
              aria-disabled="true"
              data-testid="report-maltego-guide-locked"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Maltego CE Import Guide</p>
                <button
                  type="button"
                  disabled
                  className="cursor-not-allowed rounded border border-slate-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-300"
                  title="Disabled in this release"
                >
                  Copy Import Steps
                </button>
              </div>
              <p className="mt-1 text-[10px] text-slate-500">
                Graph export / Maltego import and Export Debug JSON are disabled in this release. Use Download Final PDF.
              </p>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 shrink-0 border-t border-slate-800 bg-slate-900 p-3 md:bg-slate-900/95 md:backdrop-blur-md">
            {!canExportReport ? (
              <p
                className="mb-2 text-center text-[11px] font-semibold text-amber-300/90"
                role="status"
                data-testid="report-empty-selection-hint"
              >
                Select at least one record to generate report.
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={handleDownloadFinalPdf}
                disabled={!canExportReport}
                className="rounded-xl bg-cyan-600 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition hover:bg-cyan-500 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-cyan-600"
                data-testid="report-download-pdf"
              >
                📄 Download Final PDF
              </button>
              <button
                type="button"
                onClick={handleSendPdfTelegram}
                disabled
                className="cursor-not-allowed rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-cyan-100 opacity-35"
                data-testid="report-send-telegram"
                title="Disabled in this release"
              >
                Send Telegram
              </button>
              <button
                type="button"
                onClick={handleExportMaltego}
                disabled
                className="cursor-not-allowed rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-100 opacity-35"
                title="Disabled in this release"
                data-testid="report-export-maltego"
              >
                Export Local Graph
              </button>
              <button
                type="button"
                onClick={handleExportDebugJson}
                disabled
                className="cursor-not-allowed rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-200 opacity-35"
                title="Disabled in this release"
                data-testid="report-export-debug"
                aria-disabled="true"
              >
                ⚙️ Export Debug JSON
              </button>
              <input
                ref={debugImportInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={handleImportDebugJson}
                disabled
              />
              <button
                type="button"
                disabled
                className="cursor-not-allowed rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-200 opacity-35"
                title="Disabled in this release"
                data-testid="report-import-debug"
              >
                Import Debug JSON
              </button>
            </div>
          </div>
        </div>
      </aside>

      {pdfSharePreview?.blob ? (
        <MobilePdfShareOverlay
          blob={pdfSharePreview.blob}
          filename={pdfSharePreview.filename}
          targetLabel={String(target || '')}
          onClose={() => setPdfSharePreview(null)}
        />
      ) : null}
    </div>
  );
}
